
import { Checkbox, IconButton, MenuItem, TextField } from '@mui/material';
import { Component } from 'react';
import LocationPicker from './LocationPicker';
import { Edit } from '@mui/icons-material';
import { DOCUMENT_NAME, GENDER } from '../../../shared/constants';
import { errorToast } from '../toast';
import AttachmentInput from './AttachmentInput';
import { formatDateForDateInput, returnChangedAttributes } from '../utils';
import _ from 'lodash';

export default class CustomerForm extends Component {

   static SUBMIT_EVENT = 'submit-f2cc1aa7-424b-4116-8f8a-37f7cf4f49e7';
   static CHANGE_EVENT = 'change-f2cc1aa7-424b-4116-8f8a-37f7cf4f49e7';

   state = {
      is_company: false,
      name: '',
      surname: '',
      gender: '',
      dob: '',
      email: '',
      phone: '',
      national_id: '',
      address: '',
      gps: null,
      showLocationPicker: false,
      attachments: Object.values(DOCUMENT_NAME.CUSTOMER).map(name => ({
         name,
         base64: null,
         ext: null
      }))
   }

   toDMS(deg) {
      const d = Math.floor(deg);
      const minfloat = (deg - d) * 60;
      const m = Math.floor(minfloat);
      const sec = (minfloat - m) * 60;
      return `${d}° ${m}' ${sec}"`;
   }

   gpsToDMS({ lat, long }) {
      return `${this.toDMS(lat)} ${lat >= 0 ? 'N' : 'S'}, ${this.toDMS(long)} ${long >= 0 ? 'E' : 'W'}`;
   }

   submit = async (cb) => {

      let data = null;

      try {
         // extract and validate
         const txtName = document.getElementById('txt-name');
         const txtSurname = document.getElementById('txt-surname');
         const txtGender = document.getElementById('txt-gender');
         const txtEmail = document.getElementById('txt-email');
         const txtPhone = document.getElementById('txt-phone');
         const txtDob = document.getElementById('txt-dob');
         const txtNationalId = document.getElementById('txt-national-id');
         const txtAddress = document.getElementById('txt-address');

         const { is_company, name, surname, gender, dob, email, phone, national_id, address, gps, attachments } = this.state;
      
         if (!name) {
            errorToast("Please enter the customer's name");
            return txtName.focus();
         }

         if (!is_company && !surname) {
            errorToast("Please enter the customer's surname");
            return txtSurname.focus();
         }

         if (!is_company && !gender) {
            errorToast("Please select the customer's gender");
            return txtGender.focus();
         }

         if (!email) {
            errorToast("Please enter the customer's email");
            return txtEmail.focus();
         }

         if (!phone) {
            errorToast("Please enter the customer's phone number");
            return txtPhone.focus();
         }

         if (!is_company && !dob) {
            errorToast("Please enter the customer's date of birth");
            return txtDob.focus();
         }

         if (!is_company && !national_id) {
            errorToast("Please enter the customer's national ID");
            return txtNationalId.focus();
         }

         if (!address) {
            errorToast("Please enter the customer's address");
            return txtAddress.focus();
         }

         if (!gps) {
            errorToast("Please set the customer's location");
            return;
         }
         
         if (!this.isInEditMode()) {
            for (let attachment of attachments) {
               if (!attachment.base64) {
                  errorToast(`Please attach ${attachment.name}`);
                  return;
               }
            }
         }

         data = {
            name,
            email,
            phone,
            address: {
               text: address,
               gps
            },
         }

         if (!this.isInEditMode()) {
            data.attachments = attachments.map(attachment => ({
               name: attachment.name,
               base64: attachment.base64,
               ext: attachment.ext
            }));

            data.is_company = is_company;

         }

         if (!is_company) {
            data.surname = surname;
            data.gender = gender;
            data.dob = dob;
            data.national_id = national_id;
         }

         // check changes if in edit mode
         if (this.isInEditMode()) {
            const { customer } = this.props;
            const changedAttributes = returnChangedAttributes(data, customer);
            data = null;

            if (changedAttributes.address) {
               // double chdck if the data really changed
               if (_.isEqual(changedAttributes.address, customer.address)) {
                  delete changedAttributes.address.gps;
               } 
            }

            if (Object.keys(changedAttributes).length === 0) {
               errorToast("No changes detected");
               return;
            }

            data = changedAttributes;

         }

      } finally {
         cb(data);
      }
   }

   isInEditMode() {
      return this.props.customer && this.props.customer._id;
   }

   setValues = (values) => {
      this.setState(values);
      this.props.eventBus.emit(CustomerForm.CHANGE_EVENT, values);
   }

   componentWillUnmount() {
      this.props.eventBus.off(CustomerForm.SUBMIT_EVENT, this.submit);
   }

   componentDidMount() {
      this.props.eventBus.on(CustomerForm.SUBMIT_EVENT, this.submit);

      if (this.isInEditMode()) {
         const { customer } = this.props;
         this.setState({
            ...customer,
            address: customer.address?.text,
            gps: customer.address?.gps,
         });
      }
      
   }


   render() {
      return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
         <div className="col-span-full flex items-center">
            <Checkbox
               checked={this.state.is_company}
               onChange={e => this.setValues({ is_company: e.target.checked })}
               disabled={this.isInEditMode()}
            />
            <span>
               Customer is a company
            </span>
         </div>
         <TextField
            id="txt-name"
            label="Name"
            variant="outlined"
            size="small"
            fullWidth
            className={this.state.is_company ? 'col-span-2' : ''}
            value={this.state.name}
            onChange={e => this.setValues({ name: e.target.value })}
         />
         {
            !this.state.is_company && <>
               <TextField
                  id="txt-surname"
                  label="Surname"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={this.state.surname}
                  onChange={e => this.setValues({ surname: e.target.value })}
               />
               <TextField
                  id="txt-gender"
                  label="Gender"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={this.state.gender}
                  onChange={e => this.setValues({ gender: e.target.value })}
                  select
               >
                  {
                     Object
                        .values(GENDER)
                        .map(gender => {
                           return <MenuItem className="capitalize" key={gender} value={gender}>
                              {gender}
                           </MenuItem>
                        })
                  }
               </TextField>
            </>
         }

         <TextField
            id="txt-email"
            label="Email"
            variant="outlined"
            size="small"
            fullWidth
            value={this.state.email}
            onChange={e => this.setValues({ email: e.target.value })}
            type="email"
         />

         <TextField
            id="txt-phone"
            label="Phone"
            variant="outlined"
            size="small"
            fullWidth
            value={this.state.phone}
            onChange={e => this.setValues({ phone: e.target.value })}
            type="tel"
         />

         {
            !this.state.is_company && <TextField
               id="txt-dob"
               label="Date of Birth"
               variant="outlined"
               size="small"
               type="date"
               fullWidth
               slotProps={{
                  inputLabel: { shrink: true }
               }}
               value={formatDateForDateInput(this.state.dob)}
               onChange={e => this.setValues({ dob: e.target.value })}
            />
         }

         <TextField
            id="txt-address"
            label="Address"
            variant="outlined"
            size="small"
            fullWidth
            value={this.state.address}
            onChange={e => this.setValues({ address: e.target.value })}
         />

         <div className="rounded bg-gray-100 grid grid-cols-[1fr,auto] overflow-hidden">
            <div className="flex items-center overflow-hidden">
               <div className="overflow-hidden truncate px-4">
                  {this.state.gps ? this.gpsToDMS(this.state.gps) : 'Customer location'}
               </div>
            </div>
            <IconButton
               className="bg-orange-500 text-white rounded-none"
               onClick={() => this.setState({ showLocationPicker: true })}
            >
               <Edit />
            </IconButton>
            {
               this.state.showLocationPicker && <LocationPicker
                  onClose={gps => {
                     const update = { showLocationPicker: false }
                     if (gps)
                        update.gps = gps;
                     this.setState(update);
                  }}
                  startPosition={this.state.gps}
               />
            }
         </div>
         
         {
            !this.state.is_company && <TextField
               id="txt-national-id"
               label="National ID"
               helperText="Format: ##-#####A-##"
               variant="outlined"
               size="small"
               fullWidth
               value={this.state.national_id}
               onChange={e => this.setValues({ national_id: e.target.value })}
            />
         }

         {
            !this.isInEditMode() && <div className="col-span-full flex flex-col gap-4">
               {
                  this.state.attachments.map((attachment, index) => {
                     return <AttachmentInput
                        label={attachment.name}
                        key={attachment.name}
                        name={attachment.fileName}
                        onChange={data => {
                           const attachments = [ ...this.state.attachments ];

                           attachments[index] = {
                              ...attachments[index],
                              base64: data.base64,
                              fileName: data.name,
                              ext: data.ext
                           };

                           this.setState({ attachments });
                        }}
                     />
                  })
               }
            </div>
         }

      </div>
   }

}